import { MarketItemSkeleton } from '../market-item';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { UploadedImage } from '../uploaded-image';
import { ViewUserIcon } from '@/client/assets';
import { LocalizedText } from '@/client/components/localized-text';
import { shouldFetchSearchRecommend, useTrendingMarkets } from '@/client/hooks/use-trending-markets';
import { useModelStore } from '@/client/store/model-store';
import useSearchStore from '@/client/store/search-store';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

export const SearchRecommend = ({ setInput }: { setInput: (input: string) => void }) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const searchOpen = useModelStore(state => state.searchOpen);
  const setSearchOpen = useModelStore(state => state.setSearchOpen);
  const searchList = useSearchStore(state => state.searchList);
  const clearSearch = useSearchStore(state => state.clearSearch);
  // Recommendations = the same volume-sorted trending list the home rail
  // shows (shared query key -> opening search on home is a pure cache hit).
  // Trim to the slot count this panel has room for (fewer when recent
  // searches are also shown) -- a client-side slice, never an extra request.
  const visibleLimit = searchList.length > 0 ? 7 : 9;
  const tradingQuery = useTrendingMarkets({ enabled: shouldFetchSearchRecommend(searchOpen) });
  const tradingList = useMemo(
    () => (tradingQuery.data?.pages.flatMap(item => item.markets) ?? []).slice(0, visibleLimit),
    [tradingQuery.data?.pages, visibleLimit]
  );
  return (
    <div className="space-y-3 md:space-y-6">
      {/* history */}
      {searchList.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <h3 className="leading-3 text-foreground/70 ps-2">{t('recent-searches')}</h3>
            <span className="text-foreground/40 hover:text-foreground cursor-pointer" onClick={() => clearSearch()}>
              {t('clear')}
            </span>
          </div>
          <div className="w-full flex flex-wrap gap-2 items-center max-h-20 overflow-y-auto">
            {searchList.map((item, index) => (
              <Button
                key={`search-${item}-${index}`}
                size="sm"
                variant="outline"
                className="bg-foreground/5 rounded-full px-3 border-foreground/4 shadow-none"
                onClick={() => setInput(item)}
              >
                {item}
              </Button>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-2">
        <h3 className="text-3 leading-3 text-foreground/70 ps-2">{t('trending-labe')}</h3>
        <div className="w-full">
          {tradingList.map(item => (
            <div
              key={`market-${item.marketAddress}`}
              onClick={() => {
                setSearchOpen(false);
                navigate(`/market/${item.marketAddress}`);
              }}
              className="p-2 gap-3 min-h-12 flex items-center justify-between line-clamp-1 hover:bg-foreground/5 rounded-md"
            >
              <UploadedImage fileKey={item.marketPicUrl} className="size-8 rounded-2" />
              <TextTooltip
                tooltip={<LocalizedText i18n={item.nameI18n}>{item.marketName}</LocalizedText>}
                className="flex-1 line-clamp-1"
              >
                <LocalizedText i18n={item.nameI18n}>{item.marketName}</LocalizedText>
              </TextTooltip>
              <div className="gap-1 flex items-center text-3 flex-shrink-0">
                <ViewUserIcon className="size-3 fill-foreground/70" />
                <span className="text-foreground/70">{item.participantsCount}</span>
              </div>
            </div>
          ))}
          {tradingQuery.isLoading && <MarketItemSkeleton />}
        </div>
      </div>
    </div>
  );
};
